export type AppContext =
    | 'connect'
    | 'learn'
    | 'lead'
    | 'global'
    | 'members'
    | 'events'
    | 'resources'
    | 'concierge'
    | 'messages'
    | 'notifications'
    | 'unknown'

export interface NavItem {
    text: string
    key: string
    url: string | null
    context?: AppContext
    icon?: unknown
}

export interface UserProfile {
    id?: string
    preferredName: string
    firstName: string
    lastName: string
    profileImageUrl: string
    recordTypeName: string
}

export interface GlobalHeaderNavigateEvent {
    href: string | null
    key: string
    label: string
    context: AppContext | null
}

export interface GlobalHeaderProfileLoadedEvent {
    userId: string
    profile: UserProfile
}

export interface GlobalHeaderAuthErrorEvent {
    status: number
    message: string
}

export interface GlobalHeaderAvatarClickEvent {
    dropdownOpen: boolean
}

export interface GlobalHeaderSearchEvent {
    key: 'search'
}

export const USER_PROFILE_ATTRIBUTE = 'user-profile'

export const CONNECT_BASE_URL = import.meta.env.VITE_URL_CONNECT
export const DEFAULT_API_BASE_URL = import.meta.env.VITE_BASE_CONNECT_API
export const M2MX_PROFILE_URL = import.meta.env.VITE_URL_M2MX_PROFILE
export const M2MX_OVERVIEW_URL = import.meta.env.VITE_URL_M2MX_OVERVIEW
export const MEMBERS_URL = import.meta.env.VITE_URL_MEMBERS
export const CONCIERGE_URL = import.meta.env.VITE_URL_CONCIERGE

function withTrailingSlash(url: string | undefined): string {
    return `${(url ?? '').replace(/\/+$/, '')}/`
}

function withoutSiteSegment(url: string | undefined): string {
    return withTrailingSlash(url).replace(/\/s\/$/, '/')
}

export const GLOBAL_HEADER_ITEMS = {
    primary: [
        {text: 'Connect', key: 'connect', url: `${CONNECT_BASE_URL}/#/home`, context: 'connect'},
        {text: 'Learn', key: 'learn', url: import.meta.env.VITE_URL_LEARN, context: 'learn'},
        {text: 'Lead', key: 'lead', url: import.meta.env.VITE_URL_LEAD, context: 'lead'},
    ] as NavItem[],
    secondary: [
        {text: 'Members', key: 'members', url: MEMBERS_URL, context: 'members'},
        {text: 'Events', key: 'events', url: `${CONNECT_BASE_URL}/#/events`, context: 'events'},
    ] as NavItem[],
}

export const ICON_LINKS = {
    resources: {
        text: 'Resources',
        key: 'resources',
        url: `${CONNECT_BASE_URL}/#/resources`,
        context: 'resources',
    } as NavItem,
    concierge: {
        text: 'Concierge',
        key: 'concierge',
        url: `${withoutSiteSegment(CONCIERGE_URL)}login`,
        context: 'concierge',
    } as NavItem,
    messages: {
        text: 'Messages',
        key: 'messages',
        url: `${CONNECT_BASE_URL}/#/messenger`,
        context: 'messages',
    } as NavItem,
    notifications: {text: 'Notifications', key: 'notifications', url: null, context: 'notifications'} as NavItem,
}

export function resolveLogoutUrl(origin: string, appContext: readonly string[] = ['unknown']): string {
    if (appContext.includes('members')) {
        return `${withTrailingSlash(MEMBERS_URL)}secur/logout.jsp`
    }

    if (appContext.includes('concierge')) {
        return `${withoutSiteSegment(CONCIERGE_URL)}secur/logout.jsp`
    }

    if (appContext.includes('learn') || appContext.includes('lead') || appContext.includes('global')) {
        return `${withTrailingSlash(origin)}auth/logout`
    }

    return `${CONNECT_BASE_URL}/#/logout?redirectUrl=${origin}`
}

export function getAccountLinks(
    origin: string,
    appContext: readonly string[] = ['unknown'],
    icons?: Partial<Record<'myAccount' | 'myProfile' | 'm2mx' | 'logout', unknown>>,
): Record<string, NavItem> {
    return {
        myAccount: {
            text: 'My account',
            key: 'account',
            url: `${CONNECT_BASE_URL}/#/privacySettings/myAccount`,
            context: 'connect',
            icon: icons?.myAccount,
        },
        myProfile: {
            text: 'My profile',
            key: 'profile',
            url: `${CONNECT_BASE_URL}/#/myProfile`,
            context: 'connect',
            icon: icons?.myProfile,
        },
        m2mx: {
            text: 'M2Mx',
            key: 'm2mx',
            url: M2MX_OVERVIEW_URL,
            context: 'connect',
            icon: icons?.m2mx,
        },
        logout: {
            text: 'Log out',
            key: 'logout',
            url: resolveLogoutUrl(origin, appContext),
            context: 'connect',
            icon: icons?.logout,
        },
    }
}

export const CONTEXT_TITLES: Record<AppContext, string> = {
    connect: 'Connect',
    learn: 'Learn',
    lead: 'Lead',
    global: 'Global',
    members: 'Members',
    events: 'Events',
    resources: 'Resources',
    concierge: 'Concierge',
    messages: 'Messages',
    notifications: 'Notifications',
    unknown: 'YPO',
}

/**
 * Custom Lit boolean attribute converter that handles string "false" passed via setAttribute().
 * Standard Lit `type: Boolean` only checks attribute presence, so `show-shadow="false"`
 * would still be treated as true. This converter explicitly maps the string "false" to false.
 */
export const booleanConverter = {
    fromAttribute(val: string | null): boolean {
        if (val === null) return false
        if (val === 'false') return false
        return true
    },
    toAttribute(val: boolean): string | null {
        return val ? '' : null
    },
}

export const countConverter = {
    fromAttribute(val: string | null): number {
        if (val === null || val === '' || val === 'undefined' || val === 'null') return 0
        const n = Number(val)
        return isNaN(n) ? 0 : Math.max(0, Math.trunc(n))
    },
    toAttribute(val: number): string {
        return String(Math.max(0, val))
    },
}

export function parseUserProfileAttribute(val: string | null): UserProfile | null {
    if (val === null || val === '' || val === 'undefined' || val === 'null') return null

    try {
        return JSON.parse(val) as UserProfile
    } catch (error) {
        console.warn(`[global-ypo-header] Invalid ${USER_PROFILE_ATTRIBUTE} JSON:`, error)
        return null
    }
}

export function serializeUserProfileAttribute(val: UserProfile | null): string | null {
    return val ? JSON.stringify(val) : null
}

export function emit<T>(element: HTMLElement, name: string, detail: T): void {
    element.dispatchEvent(
        new CustomEvent<T>(name, {
            detail,
            bubbles: true,
            composed: true,
        })
    )
}

export function parseAppContextAttribute(val: string | null): AppContext[] {
    if (!val) return ['unknown']
    const parsed = val.split(',').map(part => part.trim()).filter(Boolean) as AppContext[]
    return parsed.length ? parsed : ['unknown']
}

export function serializeAppContextAttribute(val: AppContext[]): string {
    return val.join(',')
}

export function parseBooleanAttribute(val: string | null): boolean {
    if (val === null) return false
    return val !== 'false'
}

declare global {
    interface Window {
        dataLayer: Array<Record<string, unknown>>
    }
}

export function gaDataLayerEvent(eventName: string, eventValue: string, extraData: Record<string, unknown> = {}): void {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
        event: eventName,
        event_value: eventValue,
        ...extraData,
    })
}
